import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FilterListIcon from "@mui/icons-material/FilterList";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import SettingsIcon from "@mui/icons-material/Settings";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import Box from "@mui/material/Box";
import { MouseEvent, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { AUDIT_TOKENS } from "../../theme/auditTokens";
import { AuditButton } from "../common/AuditButton";
import { ToolbarIconButton } from "../common/ToolbarIconButton";

/**
 * Shown next to the title and printed on the PDF manual, which carries the same version (customer
 * request, 16.10.2026): bump with every customer-visible change and regenerate the manual with it.
 */
export const PAGE_VERSION = "2.13";

interface Props {
  /** The view switch, placed between title and actions. */
  children: ReactNode;
  /** First action of the toolbar, the group filter. */
  groupFilter: ReactNode;
  helpOpen: boolean;
  onToggleHelp: () => void;
  onRefresh: () => void;
  refreshing: boolean;
  onExport: () => void;
  exportDisabled: boolean;
  /** The export waits for every address of the rows; the button says so. */
  exportPreparing?: boolean;
  /** Grid-only actions; omitted on phones, where there is no grid. */
  columns?: {
    onOpenColumns: () => void;
    headerFiltersShown: boolean;
    onToggleHeaderFilters: () => void;
    /** Column filters in effect, shown on the toggle so hidden filters never go unnoticed. */
    activeFilterCount: number;
  };
  onOpenSettings: (anchor: HTMLElement) => void;
}

/** One line: title with version, the view switch and the page actions as icon buttons. */
export function PageHeaderBar({
  children,
  groupFilter,
  helpOpen,
  onToggleHelp,
  onRefresh,
  refreshing,
  onExport,
  exportDisabled,
  exportPreparing,
  columns,
  onOpenSettings,
}: Props) {
  const { t } = useTranslation();

  return (
    <Box
      component="header"
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "10px",
        flexWrap: { xs: "wrap", md: "nowrap" },
        mb: "8px",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "baseline", gap: "8px", flex: "0 0 auto" }}>
        <Box
          component="h1"
          sx={{ m: 0, fontSize: 17, lineHeight: 1.2, color: AUDIT_TOKENS.ink, fontWeight: 750, whiteSpace: "nowrap" }}
        >
          {t("logbookAudit.title")}
        </Box>
        <Box
          component="span"
          title={t("logbookAudit.meta.versionTitle", { version: PAGE_VERSION })}
          sx={{
            fontSize: 11,
            fontWeight: 600,
            fontVariantNumeric: "tabular-nums",
            color: AUDIT_TOKENS.muted,
            border: `1px solid ${AUDIT_TOKENS.line}`,
            borderRadius: 999,
            px: "7px",
            bgcolor: AUDIT_TOKENS.surface,
          }}
        >
          {t("logbookAudit.meta.version", { version: PAGE_VERSION })}
        </Box>
      </Box>
      <Box sx={{ flex: 1, minWidth: 0, order: { xs: 3, md: 0 }, flexBasis: { xs: "100%", md: 0 } }}>{children}</Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: "6px", ml: { xs: "auto", md: 0 } }}>
        {groupFilter}
        <ToolbarIconButton
          label={t("logbookAudit.actions.refresh")}
          icon={<RefreshIcon fontSize="small" aria-hidden />}
          onClick={onRefresh}
          disabled={refreshing}
        />
        {columns && (
          <>
            <ToolbarIconButton
              label={t("logbookAudit.actions.columns")}
              icon={<ViewColumnIcon fontSize="small" aria-hidden />}
              onClick={columns.onOpenColumns}
              aria-haspopup="dialog"
            />
            <ToolbarIconButton
              label={t(
                columns.activeFilterCount > 0 && !columns.headerFiltersShown
                  ? "logbookAudit.actions.headerFiltersActive"
                  : "logbookAudit.actions.headerFilters",
                { count: columns.activeFilterCount },
              )}
              icon={<FilterListIcon fontSize="small" aria-hidden />}
              onClick={columns.onToggleHeaderFilters}
              aria-pressed={columns.headerFiltersShown}
              active={columns.headerFiltersShown}
              badge={columns.activeFilterCount}
            />
          </>
        )}
        <ToolbarIconButton
          label={t("logbookAudit.actions.help")}
          icon={<HelpOutlineIcon fontSize="small" aria-hidden />}
          onClick={onToggleHelp}
          aria-haspopup="dialog"
          aria-expanded={helpOpen}
        />
        <ToolbarIconButton
          label={t("logbookAudit.actions.settings")}
          icon={<SettingsIcon fontSize="small" aria-hidden />}
          onClick={(event: MouseEvent<HTMLButtonElement>) => onOpenSettings(event.currentTarget)}
          aria-haspopup="dialog"
        />
        <AuditButton
          primary
          onClick={onExport}
          disabled={exportDisabled}
          aria-label={exportPreparing ? t("logbookAudit.actions.exportPreparing") : t("logbookAudit.actions.export")}
          sx={{ height: 34, px: "12px", "@media (max-width: 820px)": { minHeight: 44, height: 44, flex: "0 0 auto" } }}
        >
          <FileDownloadIcon fontSize="small" aria-hidden />
          {exportPreparing ? t("logbookAudit.actions.exportPreparing") : t("logbookAudit.actions.exportShort")}
        </AuditButton>
      </Box>
    </Box>
  );
}
